import { getMomentTz, MESApiService, spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import { type ProductForSelector, type WorkOrder } from "../models/WorkOrder";
import { toPlanChangeRefs } from "./PlanChangeRepository";
const moment = getMomentTz();

export const getWorkOrders = async (
	assetId: number,
	shiftId: number | null = null,
	startDate: Date | null = null,
	endDate: Date | null = null,
	workOrderId: number | null = null,
): Promise<WorkOrder[]> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "AssetId", value: assetId },
		{ name: "ShiftId", value: shiftId },
		{
			name: "StartDate",
			value: startDate ? moment(startDate).format("YYYY-MM-DD") : null,
		},
		{
			name: "EndDate",
			value: endDate ? moment(endDate).format("YYYY-MM-DD") : null,
		},
		{ name: "WorkOrderId", value: workOrderId },
	];
	const resp = await apiService.callV2("[FAB].[GetWorkOrders]", parameters);

	if (resp.ok) {
		const rows = get(resp, "data.tables[0].rows", []);
		return rows.map((row: WorkOrder) => ({
			...row,
			CreatedAt: row.CreatedAt ? moment.utc(row["CreatedAt"]).toDate() : null,
			StartedAt: row.StartedAt ? moment.utc(row["StartedAt"]).toDate() : null,
			CompletedAt: row.CompletedAt
				? moment.utc(row["CompletedAt"]).toDate()
				: null,
			ProductionDate: row.ProductionDate ?? null,
			ShiftEnd: row.ShiftEnd ? moment.utc(row["ShiftEnd"]).toDate() : null,
			TargetProductionDate: row.TargetProductionDate ?? null,
			TargetShiftEnd: row.TargetShiftEnd
				? moment.utc(row["TargetShiftEnd"]).toDate()
				: null,
		})) as WorkOrder[];
	}
	throw new Error(resp.message || "Error fetching work orders");
};

export const getProductsForSelector = async (
	areaAssetId: number | null = null,
): Promise<ProductForSelector[]> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "AreaAssetId", value: areaAssetId },
	];
	const resp = await apiService.callV2(
		"[FAB].[GetProductsForSelector]",
		parameters,
	);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as ProductForSelector[];
	}
	throw new Error(resp.message || "Error fetching products for selector");
};

export const upsertWorkOrder = async (workOrder: Partial<WorkOrder>) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "WorkOrderId", value: workOrder.WorkOrderId ?? null },
		{ name: "AssetId", value: workOrder.AssetId ?? null },
		{ name: "ProductId", value: workOrder.ProductId ?? null },
		{ name: "ClipId", value: workOrder.ClipId ?? null },
		{ name: "Quantity", value: workOrder.Quantity ?? null },
		{ name: "Status", value: workOrder.Status ?? null },
		{ name: "Notes", value: workOrder.Notes ?? null },
		{ name: "Ship", value: workOrder.Ship ?? null },
		{ name: "NextLocations", value: workOrder.NextLocations ?? null },
		{ name: "ShiftId", value: workOrder.ShiftId ?? null },
		{ name: "TargetShiftId", value: workOrder.TargetShiftId ?? null },
	];
	return await apiService.callV2("[FAB].[UpsertWorkOrder]", parameters);
};

export const deleteWorkOrder = async (workOrderId: number) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "WorkOrderId", value: workOrderId },
	];
	const resp = await apiService.callV2("[FAB].[DeleteWorkOrder]", parameters);

	if (!resp.ok) {
		throw new Error(resp.message || "Error deleting work order");
	}

	// Undo batch created by this delete
	return toPlanChangeRefs(get(resp, "data.tables[0].rows", []));
};

export const reorderWorkOrders = async (
	assetId: number,
	orderedIds: number[],
) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "AssetId", value: assetId },
		{ name: "OrderedIds", value: JSON.stringify(orderedIds) },
	];
	return await apiService.callV2("[FAB].[ReorderWorkOrders]", parameters);
};

// Timing correction: id + times only (+ status on complete/reopen); omitted fields keep current values
export const updateWorkOrderTiming = async (
	workOrderId: number,
	startedAt: Date | null,
	completedAt: Date | null,
	status: string | null = null,
) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "WorkOrderId", value: workOrderId },
		{ name: "StartedAt", value: startedAt ? startedAt.toISOString() : null },
		{
			name: "CompletedAt",
			value: completedAt ? completedAt.toISOString() : null,
		},
	];
	// The SP default ('') keeps the current status; an explicit NULL would bypass the sentinels
	if (status != null) parameters.push({ name: "Status", value: status });
	return await apiService.callV2("[FAB].[UpsertWorkOrder]", parameters);
};

// Atomic save for the timing editor: unlinks + links + timing upsert in one transaction
export const applyWorkOrderTiming = async (args: {
	workOrderId: number;
	startedAt: Date | null;
	completedAt: Date | null;
	status: string | null;
	applyTiming: boolean;
	unlinkBundleIds: number[];
	linkBundleIds: number[];
}) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "WorkOrderId", value: args.workOrderId },
		{
			name: "StartedAt",
			value: args.startedAt ? args.startedAt.toISOString() : null,
		},
		{
			name: "CompletedAt",
			value: args.completedAt ? args.completedAt.toISOString() : null,
		},
		{ name: "Status", value: args.status },
		{ name: "ApplyTiming", value: args.applyTiming },
		{ name: "UnlinkBundleIds", value: args.unlinkBundleIds.join(",") || null },
		{ name: "LinkBundleIds", value: args.linkBundleIds.join(",") || null },
	];
	return await apiService.callV2("[FAB].[ApplyWorkOrderTiming]", parameters);
};

export type ReadTimingResult = {
	WorkOrderId: number;
	StartedAt: Date | null;
	CompletedAt: Date | null;
	Applied: boolean;
	Message: string;
};

export const readWorkOrderTimingFromPLC = async (
	workOrderId: number,
	force: boolean,
): Promise<ReadTimingResult> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "WorkOrderId", value: workOrderId },
		{ name: "Force", value: force },
	];
	const resp = await apiService.callV2(
		"[FAB].[ReadWorkOrderTimingFromPLC]",
		parameters,
	);
	if (!resp.ok) {
		throw new Error(resp.message || "Error reading timing from the PLC");
	}
	const row = get(resp, "data.tables[0].rows[0]", null) as Record<
		string,
		unknown
	> | null;
	if (!row) throw new Error("Empty response reading timing from the PLC");
	return {
		WorkOrderId: row.WorkOrderId as number,
		StartedAt: row.StartedAt
			? moment.utc(row.StartedAt as string).toDate()
			: null,
		CompletedAt: row.CompletedAt
			? moment.utc(row.CompletedAt as string).toDate()
			: null,
		Applied: (row.Applied as boolean) ?? false,
		Message: (row.Message as string) ?? "",
	};
};

export type PendingCount = { AssetId: number; PendingCount: number };

export const getWorkOrderPendingCounts = async (
	shiftId: number | null,
): Promise<PendingCount[]> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [{ name: "ShiftId", value: shiftId }];
	const resp = await apiService.callV2(
		"[FAB].[GetWorkOrderPendingCounts]",
		parameters,
	);
	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as PendingCount[];
	}
	throw new Error(resp.message || "Error fetching pending counts");
};

export interface CalendarWorkOrder {
	WorkOrderId: number;
	WorkOrderNumber: string | null;
	AssetId: number;
	Status: string;
	Quantity: number;
	Sequence: number;
	Notes: string | null;
	PartNumber: string | null;
	ExtendedPartNumber: string | null;
	DisplayShiftId: number;
	DisplayDate: string;
	DisplayShift: string;
	DisplayProductionShift: number;
	PlanShiftId: number | null;
	PlanDate: string | null;
	PlanShift: string | null;
	IsCarriedOver: boolean;
	StartedAt: string | null;
	CompletedAt: string | null;
}

export interface WorkOrderNumberPreview {
	WorkOrderNumber: string;
}

export const getWorkOrderNumberPreview = async (
	assetId: number,
	signal?: AbortSignal,
): Promise<WorkOrderNumberPreview> => {
	const response = await new MESApiService().callV2(
		"[FAB].[GetWorkOrderNumberPreview]",
		[{ name: "AssetId", value: assetId }],
		signal,
	);
	if (!response.ok)
		throw new Error(response.message || "Error fetching work order number");
	const row = get(response, "data.tables[0].rows[0]");
	if (!row?.WorkOrderNumber)
		throw new Error("Empty work order number response");
	return { WorkOrderNumber: row.WorkOrderNumber };
};

export interface CalendarDayShift {
	Date: string;
	DefaultShiftsPerDay?: number;
	SchemeAssetId: number;
	IsOverride: boolean;
	ShiftId: number;
	ProductionShift: number;
	Shift: string;
	Start: string;
	End: string;
}

export const moveWorkOrder = async ({
	workOrderId,
	targetShiftId,
	insertAfterWorkOrderId = null,
	eventStart = null,
	eventEnd = null,
}: {
	workOrderId: number;
	// null requires eventStart: the covering shift is resolved server-side
	targetShiftId: number | null;
	insertAfterWorkOrderId?: number | null;
	eventStart?: string | null;
	eventEnd?: string | null;
}) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "WorkOrderId", value: workOrderId },
		{ name: "TargetShiftId", value: targetShiftId },
		{ name: "InsertAfterWorkOrderId", value: insertAfterWorkOrderId },
		{ name: "EventStart", value: eventStart },
		{ name: "EventEnd", value: eventEnd },
	];
	const resp = await apiService.callV2("[FAB].[MoveWorkOrder]", parameters);

	if (!resp.ok) {
		throw new Error(resp.message || "Error moving work order");
	}

	// Undo batch created by this move (empty on shift-only moves).
	return toPlanChangeRefs(get(resp, "data.tables[0].rows", []));
};
